import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X as CloseIcon, Instagram, Facebook, Youtube } from "lucide-react";
import { Button } from "./ui/button";
import Logo from "./Logo";
import CartIcon from "./CartIcon";
import { navLinks, companyInfo } from "../mock";

// X (Twitter) logo
const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21l-6.547 7.49L22 22h-6.86l-4.62-6.04L4.8 22H2.04l7.02-8.03L2 2h6.91l4.18 5.52L18.244 2Zm-1.2 18.4h1.6L7.04 3.52H5.32l11.724 16.88Z" />
  </svg>
);

// TikTok logo
const TikTokLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.8 2h-3.2v13.2a3 3 0 1 1-3-3v-3.2a6.2 6.2 0 1 0 6.2 6.2V8.6a7.4 7.4 0 0 0 4.4 1.4V6.8a4.4 4.4 0 0 1-4.4-4.4V2Z" />
  </svg>
);

// Threads logo
const ThreadsLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.6 11.1c-.1-.1-.2-.1-.3-.2-.2-3.2-2-5.1-5-5.1-1.8 0-3.3.8-4.2 2.2l1.6 1.1c.7-1 1.6-1.3 2.6-1.3 1.7 0 2.8.9 3 2.7-.8-.2-1.6-.3-2.4-.3-2.5 0-4.4 1.4-4.4 3.5 0 1.9 1.6 3.3 3.7 3.3 2.6 0 4.3-1.6 4.7-4.1.7.4 1.2 1 1.6 1.6.5 1 .5 2.5-.7 3.7-1 1-2.2 1.5-3.9 1.5-1.9 0-3.4-.6-4.4-1.8-1-1.2-1.5-2.9-1.5-5s.5-3.8 1.5-5C9.7 6.6 11.2 6 13.1 6c1.9 0 3.4.6 4.4 1.8.5.6.9 1.4 1.1 2.3l1.9-.5c-.3-1.2-.8-2.3-1.5-3.2C17.6 4.7 15.6 4 13.1 4c-2.5 0-4.5.7-5.9 2.3C5.8 7.8 5.1 9.9 5.1 12c0 2.1.7 4.2 2.1 5.7C8.6 19.3 10.6 20 13.1 20c2.3 0 4-.7 5.3-2.1 1.7-1.8 1.6-4.1.7-5.5-.4-.5-.9-1-1.5-1.3Zm-4 4c-1.1 0-1.8-.6-1.8-1.3 0-.8.8-1.4 2.3-1.4.7 0 1.4.1 2 .3-.1 1.5-.9 2.4-2.5 2.4Z" />
  </svg>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top contact bar */}
      <div className="bg-knight-navy-dark text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          {/* Far-left: toll-free + office number */}
          <div className="flex items-center gap-5 flex-wrap">
            <a href={`tel:${companyInfo.tollFree.replace(/\D/g, "")}`} className="flex items-center gap-2 hover:text-knight-gold transition-colors">
              <Phone size={14} className="text-knight-gold" />
              <span className="font-bold tracking-wide">{companyInfo.tollFree}</span>
            </a>
            <a href={`tel:${companyInfo.phones[0]}`} className="hidden sm:flex items-center gap-2 hover:text-knight-gold transition-colors">
              <Phone size={13} className="text-knight-gold" />
              <span className="font-bold tracking-wide">{companyInfo.phones[0]}</span>
            </a>
          </div>

          <div className="flex items-center gap-3">
            <a href={companyInfo.socials.instagram} aria-label="Instagram" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><Instagram size={16} /></a>
            <a href={companyInfo.socials.threads} aria-label="Threads" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><ThreadsLogo size={16} /></a>
            <a href={companyInfo.socials.facebook} aria-label="Facebook" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><Facebook size={16} /></a>
            <a href={companyInfo.socials.twitter} aria-label="X" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><XLogo size={15} /></a>
            <a href={companyInfo.socials.tiktok} aria-label="TikTok" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><TikTokLogo size={16} /></a>
            <a href={companyInfo.socials.youtube} aria-label="YouTube" className="hover:text-knight-gold hover:-translate-y-0.5 transition-all duration-200"><Youtube size={16} /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`${scrolled ? "bg-white/95 backdrop-blur shadow-md" : "bg-white"} transition-all border-b border-slate-100`}>
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
          <Link to="/" className="flex-shrink-0">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                className={({ isActive }) => `relative px-3.5 py-2 text-[13px] font-semibold tracking-wide rounded-md transition-colors ${isActive ? "text-white bg-knight-navy" : "text-knight-navy hover:bg-knight-navy/5"}`}
              >
                {link.name}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" className="border-knight-navy text-knight-navy hover:bg-knight-navy hover:text-white btn-lift">
              Client Login
            </Button>
            <Button className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-semibold btn-lift">
              Affiliate Login
            </Button>
            <CartIcon />
          </div>

          <div className="flex items-center gap-1 lg:hidden">
            <CartIcon />
            <button onClick={() => setOpen(!open)} aria-label="Toggle menu" className="p-2 text-knight-navy">
              {open ? <CloseIcon size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white">
            <div className="px-4 py-3 flex flex-col gap-1">
              {navLinks.map(link => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) => `px-3 py-2.5 rounded-md text-sm font-semibold ${isActive ? "bg-knight-navy text-white" : "text-knight-navy hover:bg-knight-navy/5"}`}
                >
                  {link.name}
                </NavLink>
              ))}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 border-knight-navy text-knight-navy">Client Login</Button>
                <Button className="flex-1 bg-knight-gold hover:bg-knight-gold/90 text-knight-navy">Affiliate Login</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
